// react
import { useState } from 'react';
// react-hook-form
import { Controller, useForm, useWatch } from 'react-hook-form';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';

// material ui
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Slider,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

// shared components
import { CancelButton, SubmitButton } from '@/components/ui/button';
import { RHFFieldLabel, RHFTextField } from '@/components/ui/form';

// components
import {
  getGroupedOptions,
  getTypeColor,
  getTypeIcon,
} from '@/utils/nodeTypeMapper';

// relative
import InitiativeInclusionNotice from '../components/tree/InitiativeInclusionNotice';
import { mapUnitsToDataStreams } from '../utils/energyTreeFunctions';
import { toAddNodeFormValues } from './addNodeForm.mappers';
import type { AddNodeFormValues } from './addNodeForm.schema';
import { createAddNodeFormSchema } from './addNodeForm.schema';

// libraries
import { zodResolver } from '@hookform/resolvers/zod';

interface Unit {
  UnitId: number;
  Name: string;
  Tags?: Array<{ TagId: number; Text: string }>;
  UnitSensors?: Array<{ Sensor?: { SensorId: number } }>;
  usedInNode?: string;
  notSupportedReason?: string;
}

interface Inclusion {
  viaNode?: unknown;
}

type AddNodeFormProps = {
  units: Unit[];
  virtualInitiativeInclusions: Inclusion[];
  onSubmit: (values: AddNodeFormValues) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
};

export default function AddNodeForm({
  units,
  virtualInitiativeInclusions,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: AddNodeFormProps) {
  const { t } = useTranslation(['energyxplorer-tree-tab', 'validation']);

  const [searchText, setSearchText] = useState('');

  const schema = createAddNodeFormSchema(t);
  const {
    control,
    handleSubmit,
    setValue,
    formState: { isValid },
  } = useForm<AddNodeFormValues>({
    resolver: zodResolver(schema),
    defaultValues: toAddNodeFormValues(),
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const groupedOptions = getGroupedOptions(t);
  const sourceType = useWatch({ control, name: 'sourceType' });
  const specialNodeType = useWatch({
    control,
    name: 'options.specialNodeType',
  });

  const onSubmitForm = handleSubmit(async (values) => {
    if (!values.type) {
      return;
    }
    await onSubmit(values);
  });

  return (
    <form onSubmit={onSubmitForm} noValidate>
      <Tabs
        value={sourceType}
        onChange={(_, newValue) => {
          setValue('sourceType', newValue, { shouldValidate: true });
          // Clear fields when switching tabs
          if (newValue === 'dataStreams') {
            setValue('options.specialNodeType', null, { shouldValidate: true });
            setValue('options.consumption', null, { shouldValidate: true });
            setValue('options.percentage', null, { shouldValidate: true });
          } else if (newValue === 'virtual') {
            setValue('dataStreams', [], { shouldValidate: true });
            setValue('options.specialNodeType', null, { shouldValidate: true });
            setValue('options.consumption', null, { shouldValidate: true });
            setValue('options.percentage', null, { shouldValidate: true });
          } else if (newValue === 'residual') {
            setValue('dataStreams', [], { shouldValidate: true });
            setValue('options.specialNodeType', 'RestResidual', {
              shouldValidate: true,
            });
          }
        }}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTabs-indicator': {
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: 'transparent',
            bottom: 1,
          },
          '& .MuiTabs-indicatorSpan': {
            height: 4,
            width: '100%',
            borderRadius: 5,
            backgroundColor: '#c2cc00',
          },
        }}
        slotProps={{
          indicator: {
            children: <span className="MuiTabs-indicatorSpan" />,
          },
        }}
      >
        <Tab
          data-testid="add-node-tab-datastreams"
          label="Data Node"
          value="dataStreams"
          disableRipple
          sx={{ textTransform: 'none' }}
        />
        <Tab
          data-testid="add-node-tab-virtual"
          label="Virtuel Node"
          value="virtual"
          disableRipple
          sx={{ textTransform: 'none' }}
        />
        <Tab
          data-testid="add-node-tab-residual"
          label="Residual Node"
          value="residual"
          disableRipple
          sx={{ textTransform: 'none' }}
        />
      </Tabs>

      <Stack spacing={2} sx={{ mt: 2 }}>
        {sourceType === 'dataStreams' && (
          <Alert severity="info">
            {t('add_node_dialog.data_sources_tab_description', {
              ns: 'energyxplorer-tree-tab',
            })}
          </Alert>
        )}

        {sourceType === 'virtual' && (
          <Alert severity="info">
            {t('add_node_dialog.virtual_tab_description', {
              ns: 'energyxplorer-tree-tab',
            })}
          </Alert>
        )}

        {sourceType === 'residual' && (
          <Alert severity="info">
            {t('add_node_dialog.residual_tab_description', {
              ns: 'energyxplorer-tree-tab',
            })}
          </Alert>
        )}

        <RHFTextField
          data-testid="add-node-name-input"
          name="name"
          fullWidth
          control={control}
          label={t('add_node_dialog.name_label', {
            ns: 'energyxplorer-tree-tab',
          })}
          helperText={t('add_node_dialog.name_helper', {
            ns: 'energyxplorer-tree-tab',
          })}
          required
        />

        <Controller
          name="type"
          control={control}
          render={({ field, fieldState }) => {
            const allOptions = Object.values(groupedOptions).flatMap(
              (group) => group.options,
            );
            const selectedOption = allOptions.find(
              (opt) => opt.value === field.value,
            );

            return (
              <Box>
                <RHFFieldLabel required>
                  {t('add_node_dialog.type_label', {
                    ns: 'energyxplorer-tree-tab',
                  })}
                </RHFFieldLabel>
                <Autocomplete
                  data-testid="add-node-type-select"
                  value={selectedOption || null}
                  onChange={(_, newValue) => {
                    field.onChange(newValue?.value || '');
                  }}
                  options={allOptions}
                  groupBy={(option) => {
                    // Find which group this option belongs to
                    for (const groupData of Object.values(groupedOptions)) {
                      if (
                        groupData.options.some(
                          (opt: { value: string; label: string }) =>
                            opt.value === option.value,
                        )
                      ) {
                        return groupData.label;
                      }
                    }
                    return '';
                  }}
                  onBlur={field.onBlur}
                  getOptionLabel={(option) => option.label || ''}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  renderGroup={(params) => (
                    <li key={params.key}>
                      <ListSubheader sx={{ bgcolor: 'background.paper' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            py: 1,
                          }}
                        >
                          <CategoryRoundedIcon fontSize="small" />
                          <Typography fontWeight="bold">
                            {params.group}
                          </Typography>
                        </Box>
                      </ListSubheader>
                      <ul style={{ padding: 0 }}>{params.children}</ul>
                    </li>
                  )}
                  renderOption={(props, option) => {
                    const { key, ...rest } = props;
                    return (
                      <li key={key} {...rest}>
                        <Box display="flex" alignItems="center" width="100%">
                          <Box
                            sx={{
                              backgroundColor: getTypeColor(option.value),
                              color: 'white',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              mr: 1,
                              height: 28,
                              width: 28,
                              justifyContent: 'center',
                              fontSize: '14px',
                            }}
                          >
                            <i
                              className={`fa-solid ${getTypeIcon(option.value)}`}
                            />
                          </Box>
                          <Typography>{option.label}</Typography>
                        </Box>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="filled"
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error?.message ||
                        t('add_node_dialog.type_helper', {
                          ns: 'energyxplorer-tree-tab',
                        })
                      }
                      required
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          disableUnderline: true,
                          sx: { borderRadius: 1 },
                        },
                        htmlInput: {
                          ...params.inputProps,
                        },
                      }}
                    />
                  )}
                />
              </Box>
            );
          }}
        />

        <Box>
          {sourceType === 'dataStreams' && (
            <Controller
              name="dataStreams"
              control={control}
              render={({ field, fieldState }) => (
                <Box>
                  <RHFFieldLabel required>
                    {t('add_node_dialog.data_source_label', {
                      ns: 'energyxplorer-tree-tab',
                    })}
                  </RHFFieldLabel>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    size="small"
                    onBlur={field.onBlur}
                    options={units || []}
                    getOptionLabel={(option) =>
                      option ? `${option.UnitId} - ${option.Name}` : ''
                    }
                    value={
                      field.value
                        .map((ds) =>
                          units.find((unit) => unit.UnitId === ds.unitId),
                        )
                        .filter(Boolean) as Unit[]
                    }
                    inputValue={searchText}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason !== 'reset') setSearchText(newInputValue);
                    }}
                    getOptionDisabled={(option) =>
                      !!option?.usedInNode || !!option?.notSupportedReason
                    }
                    onClose={() => setSearchText('')}
                    onChange={(e, newValue) => {
                      field.onChange(mapUnitsToDataStreams(newValue));
                      // Mark field as touched to enable validation
                      field.onBlur();
                    }}
                    filterSelectedOptions
                    filterOptions={(options, { inputValue }) => {
                      const search = inputValue.trim().toLowerCase();
                      return options.filter((unit) => {
                        if (!unit) return false;
                        const nameMatch =
                          unit.Name?.toLowerCase().includes(search);
                        const tagMatch = unit.Tags?.some((tag) =>
                          tag.Text?.toLowerCase().includes(search),
                        );
                        const sensorMatch = unit.UnitSensors?.some((s) =>
                          String(s.Sensor?.SensorId).includes(search),
                        );
                        const unitIdMatch = String(unit.UnitId).includes(
                          search,
                        );
                        return (
                          nameMatch || tagMatch || sensorMatch || unitIdMatch
                        );
                      });
                    }}
                    renderOption={(props, option) => {
                      if (!option) return null;
                      const { key, ...rest } = props;

                      return (
                        <li key={key} {...rest}>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                              width: '100%',
                            }}
                          >
                            <Typography>
                              ({option.UnitId}) {option.Name}
                            </Typography>

                            {option.Tags && option.Tags.length > 0 && (
                              <Box>
                                {option.Tags.map((tag) => (
                                  <Chip
                                    key={tag.TagId}
                                    label={tag.Text}
                                    size="small"
                                    sx={{ mr: 0.5 }}
                                  />
                                ))}
                              </Box>
                            )}

                            {option.usedInNode && (
                              <Typography
                                variant="caption"
                                sx={{ color: 'gray' }}
                              >
                                {t('nodes.add_dialog.applied_for', {
                                  ns: 'energyxplorer',
                                })}{' '}
                                {option.usedInNode}
                              </Typography>
                            )}

                            {option.notSupportedReason && (
                              <Typography
                                variant="caption"
                                sx={{ color: 'red' }}
                              >
                                {option.notSupportedReason}
                              </Typography>
                            )}
                          </Box>
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        data-testid="add-node-data-streams-input"
                        error={!!fieldState.error}
                        onBlur={field.onBlur}
                        helperText={
                          fieldState.error?.message ||
                          t('add_node_dialog.data_source_helper', {
                            ns: 'energyxplorer-tree-tab',
                          })
                        }
                      />
                    )}
                    renderValue={(value, getItemProps) =>
                      value.map((option, index) => {
                        if (!option) return null;
                        const { key, ...tagProps } = getItemProps({
                          index,
                        });
                        return (
                          <Chip
                            sx={{ borderRadius: 1 }}
                            size="small"
                            key={key}
                            label={`${option.Name}`}
                            {...tagProps}
                          />
                        );
                      })
                    }
                  />
                </Box>
              )}
            />
          )}

          {sourceType === 'virtual' && null}

          {sourceType === 'residual' && (
            <Stack spacing={2}>
              <Controller
                name="options.specialNodeType"
                control={control}
                render={({ field, fieldState }) => (
                  <Box>
                    <RHFFieldLabel required>
                      {t('add_node_dialog.type_label', {
                        ns: 'energyxplorer-tree-tab',
                      })}
                    </RHFFieldLabel>
                    <TextField
                      data-testid="add-node-residual-type-select"
                      select
                      value={field.value || ''}
                      onChange={(e) => {
                        const next = e.target.value;
                        field.onChange(next);
                        // Clear conditional fields when switching type
                        if (next !== 'FlatResidual') {
                          setValue('options.consumption', null);
                        }
                        if (next !== 'PercentageResidual') {
                          setValue('options.percentage', null);
                        }
                      }}
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error?.message ||
                        t('add_node_dialog.residual_type_helper', {
                          ns: 'energyxplorer-tree-tab',
                        })
                      }
                      fullWidth
                      variant="filled"
                      slotProps={{
                        input: {
                          disableUnderline: true,
                          sx: { borderRadius: 1 },
                        },
                      }}
                    >
                      <MenuItem value="RestResidual">
                        {t('add_node_dialog.residual_rest_value', {
                          ns: 'energyxplorer-tree-tab',
                        })}
                      </MenuItem>
                      <MenuItem value="FlatResidual">
                        {t('add_node_dialog.residual_fixed_value', {
                          ns: 'energyxplorer-tree-tab',
                        })}{' '}
                        {t('nodes.add_dialog.kw', {
                          ns: 'energyxplorer',
                        })}
                      </MenuItem>
                      <MenuItem value="PercentageResidual">
                        {t('add_node_dialog.residual_percentage_value', {
                          ns: 'energyxplorer-tree-tab',
                        })}{' '}
                        {t('nodes.add_dialog.percent', {
                          ns: 'energyxplorer',
                        })}
                      </MenuItem>
                    </TextField>
                  </Box>
                )}
              />

              {specialNodeType === 'FlatResidual' && (
                <Controller
                  name="options.consumption"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Box>
                      <RHFFieldLabel required>
                        {t('add_node_dialog.residual_fixed_value', {
                          ns: 'energyxplorer-tree-tab',
                        })}{' '}
                        {t('nodes.add_dialog.kw', {
                          ns: 'energyxplorer',
                        })}
                      </RHFFieldLabel>
                      <TextField
                        data-testid="add-node-consumption-input"
                        type="number"
                        value={field.value ?? ''}
                        onChange={(e) => {
                          field.onChange(
                            e.target.value === ''
                              ? null
                              : Number(e.target.value),
                          );
                        }}
                        variant="filled"
                        error={!!fieldState.error}
                        helperText={
                          fieldState.error?.message ||
                          t('add_node_dialog.residual_fixed_value_helper', {
                            ns: 'energyxplorer-tree-tab',
                          })
                        }
                        fullWidth
                        slotProps={{
                          input: {
                            disableUnderline: true,
                            sx: { borderRadius: 1 },
                          },

                          htmlInput: { min: 0, step: 'any' },
                        }}
                      />
                    </Box>
                  )}
                />
              )}

              {specialNodeType === 'PercentageResidual' && (
                <Controller
                  name="options.percentage"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Box>
                      <RHFFieldLabel>
                        {t('add_node_dialog.residual_percentage_value', {
                          ns: 'energyxplorer-tree-tab',
                        })}{' '}
                        {t('nodes.add_dialog.percent', {
                          ns: 'energyxplorer',
                        })}
                      </RHFFieldLabel>
                      <Box display="flex" alignItems="center" gap={2} m={1}>
                        <Slider
                          value={(field.value || 0) * 100}
                          onChange={(_, newVal) =>
                            field.onChange((newVal as number) / 100)
                          }
                          min={0.1}
                          max={100}
                          step={0.1}
                        />
                        <TextField
                          data-testid="add-node-percentage-input"
                          type="number"
                          hiddenLabel
                          value={((field.value ?? 0) * 100).toFixed(1)}
                          onChange={(e) => {
                            const pct = parseFloat(e.target.value);
                            const clamped = isNaN(pct)
                              ? 0
                              : Math.min(Math.max(pct, 0), 100);
                            field.onChange(clamped / 100);
                          }}
                          sx={{ width: 100 }}
                          variant="standard"
                          slotProps={{
                            input: {
                              disableUnderline: true,
                              endAdornment: (
                                <InputAdornment position="end">
                                  <PercentRoundedIcon />
                                </InputAdornment>
                              ),
                            },
                          }}
                        />
                      </Box>
                      {fieldState.error && (
                        <Typography variant="caption" color="error">
                          {fieldState.error.message}
                        </Typography>
                      )}
                    </Box>
                  )}
                />
              )}
            </Stack>
          )}
        </Box>

        <InitiativeInclusionNotice
          virtualInitiativeInclusions={virtualInitiativeInclusions}
          mode="add"
        />

        <Box display="flex" justifyContent="flex-end" gap={1}>
          <CancelButton onClick={onCancel} disabled={isSubmitting}>
            {t('add_node_dialog.cancel', { ns: 'energyxplorer-tree-tab' })}
          </CancelButton>
          <SubmitButton disabled={!isValid || isSubmitting}>
            {t('common:actions.submit')}
          </SubmitButton>
        </Box>
      </Stack>
    </form>
  );
}
